import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import authenticatedAxios from '../../authenticatedAxios';
import { baseurl } from '../../api';
import './Accounting.css';

const AccountingConfigurationDetail = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const response = await authenticatedAxios.get(`${baseurl}accounting/master-accounting-configuration/`);
      if (response?.data && response?.data?.data) {
        setConfig(response.data.data);
      } else {
        setConfig(null);
      }
    } catch (error) {
      console.error('Error fetching master accounting configuration:', error);
      if (error?.response?.data?.error_code === 1001) {
        navigate("/permission-denied");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const renderValue = (val) => {
    if (val) {
      return <div className="accounting-view-box">{val}</div>;
    }
    return <div className="accounting-view-box accounting-view-empty">Not configured</div>;
  };

  if (loading) {
    return (
      <div className="whole-container">
        <PageHeader pageName="Accounting /" nestedpageName="Configurations" linkTo="/accounting/configuration" />
        <div className="accounting-config-container">
          <p className="common-text fts-14 ftw-4">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader pageName="Accounting /" nestedpageName="Configurations" linkTo="/accounting/configuration" />
      </div>

      <div className="accounting-config-container">
        <div className="accounting-header-row">
          <div className="accounting-sub-header">
            Manage the default accounting configuration for your organization.
          </div>
          <button
            type="button"
            className="accounting-btn-edit"
            onClick={() => navigate('/accounting/configuration/edit')}
          >
            Edit
          </button>
        </div>

        {/* DEFAULT JOURNALS */}
        <div className="accounting-card">
          <div className="accounting-card-title">DEFAULT JOURNALS</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default Sales Journal</label>
            {renderValue(config?.sales_journal_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default Purchase Journal</label>
            {renderValue(config?.purchase_journal_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default Bank Journal</label>
            {renderValue(config?.bank_journal_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default Cash Journal</label>
            {renderValue(config?.cash_journal_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default Miscellaneous Journal</label>
            {renderValue(config?.miscellaneous_journal_name)}
          </div>
        </div>

        {/* CURRENCY */}
        <div className="accounting-card">
          <div className="accounting-card-title">CURRENCY</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Exchange Gain Account</label>
            {renderValue(config?.exchange_gain_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Exchange Loss Account</label>
            {renderValue(config?.exchange_loss_account_name)}
          </div>
        </div>

        {/* INVENTORY */}
        <div className="accounting-card">
          <div className="accounting-card-title">INVENTORY</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Stock Valuation Account</label>
            {renderValue(config?.stock_valuation_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Opening Stock Adjustment Account</label>
            {renderValue(config?.stock_adjustment_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">COGS Account</label>
            {renderValue(config?.cogs_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default Income Account</label>
            {renderValue(config?.default_income_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">GRNI Account</label>
            {renderValue(config?.grni_account_name)}
          </div>
        </div>

        {/* RECEIVABLES & PAYABLES */}
        <div className="accounting-card">
          <div className="accounting-card-title">RECEIVABLES &amp; PAYABLES</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default Accounts Receivable</label>
            {renderValue(config?.default_accounts_receivable_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default Accounts Payable</label>
            {renderValue(config?.default_accounts_payable_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Customer Advance Account</label>
            {renderValue(config?.customer_advance_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Vendor Advance Account</label>
            {renderValue(config?.vendor_advance_account_name)}
          </div>
        </div>

        {/* TAXES */}
        <div className="accounting-card">
          <div className="accounting-card-title">TAXES</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Tax Payable Account</label>
            {renderValue(config?.tax_payable_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Tax Receivable Account</label>
            {renderValue(config?.tax_receivable_account_name)}
          </div>
        </div>

        {/* OTHERS */}
        <div className="accounting-card">
          <div className="accounting-card-title">OTHERS</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Suspense Account</label>
            {renderValue(config?.suspense_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Retained Earnings Account</label>
            {renderValue(config?.retained_earnings_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Rounding Account</label>
            {renderValue(config?.rounding_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Opening Balance Account</label>
            {renderValue(config?.opening_balance_equity_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Discount Expense Account</label>
            {renderValue(config?.discount_expense_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Discount Income Account</label>
            {renderValue(config?.discount_income_account_name)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingConfigurationDetail;
